/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import useJoinedRef from '@mc/hooks/useJoinedRef';
import { StackLayout } from '@mc/wink';
import useId from '@mc/hooks/useId';
import { useDsTranslateMessage } from '@mc/wink/internationalization/useDsTranslateMessage';
import { ariaDescribedByIds, ariaLabelledByIds } from '../utils';
import Tooltip from '../Tooltip';
import stylesheet from './Slider.css';

const getAllTicks = (
  min: $TSFixMe,
  max: $TSFixMe,
  step: $TSFixMe,
  type?: string,
) => {
  const allTicks = [];
  if (type === 'discrete') {
    for (let i = min; i <= max; i += 1) {
      allTicks.push(i);
    }
  } else {
    for (let i = min; i <= max; i += step) {
      allTicks.push(i);
    }
  }
  return allTicks;
};

export type SliderProps = {
  _?: $TSFixMe; // TODO: (props, propName, componentName) => { if (!props.label && !props['aria-labelledby']) { return new Error(formatError(ERROR_MUST_PROVIDE_LABEL, componentName)); } }
  anchorIcons?: $TSFixMe[];
  'aria-labelledby'?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  error?: string;
  helpText?: React.ReactNode;
  hideLabel?: boolean;
  hideOutput?: boolean;
  label?: React.ReactNode;
  max: number;
  min?: number;
  onBlur?: $TSFixMeFunction;
  onChange: $TSFixMeFunction;
  onFocus?: $TSFixMeFunction;
  readOnly?: boolean;
  renderOutput?: $TSFixMeFunction;
  step?: number;
  value: number | { min: number; max: number };
  type?: 'continuous' | 'dual' | 'discrete' | 'displayed_value';
  showToolTip?: boolean;
};

const Slider = React.forwardRef<$TSFixMe, SliderProps>(function Slider(
  {
    'aria-labelledby': ariaLabelledBy,
    anchorIcons = [],
    children,
    // @ts-expect-error TS(2339) FIXME: Property 'className' does not exist on type 'Props... Remove this comment to see the full error message
    className,
    disabled = false,
    readOnly = false,
    helpText,
    error,
    step = 1,
    min = 0,
    max,
    hideLabel,
    hideOutput,
    label,
    // Unused in Slider but may be provided via FormField.
    // @ts-expect-error TS(2339) FIXME: Property 'miscText' does not exist on type 'Props'... Remove this comment to see the full error message
    // eslint-disable-next-line react/prop-types
    miscText,
    onChange,
    onBlur = () => {},
    onFocus = () => {},
    value,
    type = 'displayed_value',
    renderOutput = (v) => {
      return typeof v === 'number' ? v : v ? `${v.min} - ${v.max}` : v;
    },
    showToolTip = true,
    ...props
  },
  forwardedRef,
) {
  const [isActive, setIsActive] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const rangeRef = useJoinedRef(forwardedRef, ref);
  const id = useId();
  const labelId = useId();
  const helpTextId = useId();
  const miscTextId = useId();
  const [PrefixIcon, SuffixIcon] = anchorIcons;
  // Since value can now be number or an object, we have to bypass the TS error
  const fillWidth =
    typeof value === 'number' ? ((value - min) / (max - min)) * 100 : 0;

  const allTicks = getAllTicks(min, max, step, type);
  const ticksByValue = {};

  // Double slider variables
  const initialMinVal = min;
  const initialMaxVal = max;

  const [sliderMinValue] = useState<number>(initialMinVal);
  const [sliderMaxValue] = useState<number>(initialMaxVal);

  const minInitialState = () => {
    if (value && typeof value === 'object' && 'min' in value) {
      return value.min;
    }
    return value;
  };

  const maxInitialState = () => {
    if (value && typeof value === 'object' && 'max' in value) {
      return value.max;
    }
    return value;
  };

  const [minVal, setMinVal] = useState(minInitialState);
  const [maxVal, setMaxVal] = useState(maxInitialState);
  const minGap = 1;

  const outputSelectedLabelText = useDsTranslateMessage({
    id: 'mcds.slider.output_selected_label_text',
    defaultMessage: 'Selected value:',
  });

  const slideMin = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const svalue = parseInt(e.target.value, 10);
    if (svalue >= sliderMinValue && maxVal - svalue >= minGap) {
      setMinVal(svalue);
    }
  };

  const slideMax = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const smxvalue = parseInt(e.target.value, 10);
    if (smxvalue <= sliderMaxValue && smxvalue - minVal >= minGap) {
      setMaxVal(smxvalue);
    }
  };

  const stopDrag = (): void => {
    onChange({ min: minVal, max: maxVal });
    setIsActive(false);
  };

  React.Children.forEach(children, (child) => {
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    ticksByValue[(child as $TSFixMe).props.value] = (child as $TSFixMe).props;
  });

  const setSliderTrack = (): void => {
    const range = ref.current;

    if (range && type !== 'discrete') {
      if (type === 'dual') {
        const minPercent =
          ((minVal - sliderMinValue) / (sliderMaxValue - sliderMinValue)) * 100;
        const maxPercent =
          ((maxVal - sliderMinValue) / (sliderMaxValue - sliderMinValue)) * 100;

        range.style.left = `${minPercent}%`;
        range.style.right = `${100 - maxPercent}%`;
      } else {
        range.style.left = `${fillWidth}%`;
        range.style.width = `${100 - fillWidth}%`;
      }
    }
  };

  useEffect(() => {
    setSliderTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, minVal, maxVal]);

  return (
    <div
      className={cx(stylesheet.root, className, stylesheet[type], {
        [stylesheet.error]: !!error,
      })}
    >
      <div className={stylesheet.before}>
        {label && (
          <label
            className={cx(
              'mcds-label-default',
              hideLabel && 'wink-visually-hidden',
              stylesheet.topLabel,
              disabled && stylesheet.disabled,
            )}
            id={labelId}
            htmlFor={type !== 'dual' ? id : `${id}-min`}
          >
            {label}
          </label>
        )}
        {type === 'dual' && label && (
          <label
            className="wink-visually-hidden"
            id={`${labelId}-max`}
            htmlFor={`${id}-max`}
          >
            {label}
          </label>
        )}
        {/* <output> is implemented as an aria-live region by some browsers and will re-announce this as value changes. */}
        {type === 'displayed_value' && (
          <output
            id={miscTextId}
            // className={cx({
            //   'wink-visually-hidden': hideOutput,
            // }, {
            //   stylesheet.disabled: disabled
            // })}
            className={cx(
              hideOutput && 'wink-visually-hidden',
              disabled && stylesheet.disabled,
            )}
          >
            <span className="wink-visually-hidden">
              {outputSelectedLabelText}
            </span>
            {renderOutput(value)}
          </output>
        )}
      </div>
      <div className={stylesheet.rangeContainer}>
        <StackLayout gap={0} className={stylesheet.rangeContainerBlock}>
          {type !== 'displayed_value' && (
            <>
              {!(PrefixIcon || SuffixIcon) && (
                <span
                  className={cx(
                    stylesheet.prefixVal,
                    disabled && stylesheet.disabled,
                  )}
                >
                  {min}
                </span>
              )}
              {PrefixIcon &&
                (React.isValidElement(PrefixIcon) ||
                typeof PrefixIcon === 'string' ? (
                  <span
                    className={cx(
                      stylesheet.prefixVal,
                      disabled && stylesheet.disabled,
                    )}
                  >
                    {' '}
                    {PrefixIcon}{' '}
                  </span>
                ) : (
                  <span
                    className={cx(
                      stylesheet.prefixVal,
                      disabled && stylesheet.disabledIcon,
                    )}
                  >
                    <PrefixIcon />
                  </span>
                ))}
            </>
          )}
          <div
            className={cx(stylesheet.range, stylesheet[type], {
              [stylesheet.withAnchorIcons]: anchorIcons.length > 0,
            })}
          >
            {type !== 'dual' ? (
              <>
                <div className={stylesheet.slidertrack} ref={rangeRef}></div>
                <input
                  type="range"
                  className={cx(
                    stylesheet.slider,
                    stylesheet['progress-indicator'],
                    stylesheet[type],
                  )}
                  id={id}
                  // We need to handle three cases:
                  //
                  // 1. Only pass a `label`. Since we're using a native label element,
                  //    pointing `aria-labelledby` to the existing label element is
                  //    unnecessary.
                  // 2. Only pass an `aria-labelledby`. We don't render a label
                  //    element.
                  // 3. Pass both a `label` and `aria-labelledby`. We refer to both in
                  //    the `aria-labelledby` attribute.
                  aria-labelledby={ariaLabelledByIds(
                    ariaLabelledBy,
                    ariaLabelledBy && label && labelId,
                  )}
                  aria-describedby={ariaDescribedByIds(
                    (error || helpText) && helpTextId,
                    miscText && miscTextId,
                  )}
                  value={typeof value === 'number' ? value : 0}
                  onChange={(event) => {
                    onChange(Number(event.target.value));
                  }}
                  min={min}
                  max={max}
                  step={step}
                  disabled={disabled}
                  readOnly={readOnly}
                  list={id + '-ticks'}
                  {...props}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  onMouseDown={() => setIsActive(true)}
                  onMouseUp={() => setIsActive(false)}
                  tabIndex={0}
                />
                {children && <datalist id={id + '-ticks'}>{children}</datalist>}
                <div className={stylesheet.ticks}>
                  {allTicks.map((tick) =>
                    type === 'discrete' ? (
                      <Tooltip
                        key={tick}
                        label={
                          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                          ticksByValue[tick]?.label ||
                          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                          ticksByValue[tick]?.value ||
                          tick
                        }
                        forceVisible={showToolTip && isActive && tick === value}
                        direction="top"
                        type="label"
                      >
                        <div
                          style={{
                            visibility:
                              (tick - min) % step === 0 &&
                              (!disabled || (disabled && value !== tick))
                                ? 'visible'
                                : 'hidden',
                          }}
                          className={stylesheet.tick}
                        />
                      </Tooltip>
                    ) : // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                    ticksByValue[tick] ? (
                      <Tooltip
                        key={tick}
                        label={
                          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                          ticksByValue[tick]?.label ||
                          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                          ticksByValue[tick]?.value ||
                          tick
                        }
                        forceVisible={showToolTip && isActive && tick === value}
                        direction="top"
                        type="label"
                      >
                        <div key={tick} className={stylesheet.tick} />
                      </Tooltip>
                    ) : (
                      <Tooltip
                        key={tick}
                        label={
                          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                          ticksByValue[tick]?.label ||
                          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                          ticksByValue[tick]?.value ||
                          tick
                        }
                        forceVisible={showToolTip && isActive && tick === value}
                        direction="top"
                        type="label"
                      >
                        <div
                          key={tick}
                          style={{
                            visibility: 'hidden',
                          }}
                          className={stylesheet.tick}
                        />
                      </Tooltip>
                    ),
                  )}
                </div>
              </>
            ) : (
              <div className={cx(stylesheet.doublesliderbox)}>
                <div className={stylesheet.rangeslider}>
                  <div
                    className={cx(stylesheet.doubleSliderTrack, {
                      [stylesheet.disabled]: disabled,
                    })}
                    ref={rangeRef}
                  ></div>
                  <input
                    type="range"
                    id={`${id}-min`}
                    aria-labelledby={ariaLabelledByIds(
                      ariaLabelledBy,
                      ariaLabelledBy && label && labelId,
                    )}
                    aria-describedby={ariaDescribedByIds(
                      (error || helpText) && helpTextId,
                      miscText && miscTextId,
                    )}
                    min={sliderMinValue}
                    max={sliderMaxValue}
                    value={minVal}
                    onMouseDown={() => setIsActive(true)}
                    onMouseUp={stopDrag}
                    onTouchStart={() => setIsActive(true)}
                    onTouchEnd={stopDrag}
                    className={stylesheet.minval}
                    step={step}
                    disabled={disabled}
                    readOnly={readOnly}
                    onChange={slideMin}
                    {...props}
                  />
                  <input
                    type="range"
                    id={`${id}-max`}
                    aria-labelledby={ariaLabelledByIds(
                      ariaLabelledBy,
                      ariaLabelledBy && label && `${labelId} ${labelId}-max`,
                    )}
                    min={sliderMinValue}
                    max={sliderMaxValue}
                    value={maxVal}
                    onMouseDown={() => setIsActive(true)}
                    onMouseUp={stopDrag}
                    onTouchStart={() => setIsActive(true)}
                    onTouchEnd={stopDrag}
                    className={stylesheet.maxval}
                    step={step}
                    disabled={disabled}
                    readOnly={readOnly}
                    onChange={slideMax}
                    {...props}
                  />
                  <div className={stylesheet.ticks}>
                    {allTicks.map((tick) => (
                      <Tooltip
                        key={tick}
                        label={tick}
                        forceVisible={
                          isActive && (tick === minVal || tick === maxVal)
                        }
                        direction="top"
                        type="label"
                      >
                        <div
                          key={tick}
                          style={{
                            visibility: 'hidden',
                          }}
                          className={stylesheet.tick}
                        />
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          {type !== 'displayed_value' && (
            <>
              {!(PrefixIcon || SuffixIcon) && (
                <span
                  className={cx(
                    stylesheet.suffixVal,
                    disabled && stylesheet.disabled,
                  )}
                >
                  {max}
                </span>
              )}
              {SuffixIcon &&
                (React.isValidElement(SuffixIcon) ||
                typeof SuffixIcon === 'string' ? (
                  <span
                    className={cx(
                      stylesheet.suffixVal,
                      disabled && stylesheet.disabled,
                    )}
                  >
                    {' '}
                    {SuffixIcon}{' '}
                  </span>
                ) : (
                  <span
                    className={cx(
                      stylesheet.suffixVal,
                      disabled && stylesheet.disabledIcon,
                    )}
                  >
                    <SuffixIcon />
                  </span>
                ))}
            </>
          )}
        </StackLayout>
      </div>
      {error ? (
        <span id={helpTextId} className={stylesheet.errorMessage}>
          {error}
        </span>
      ) : helpText ? (
        <span
          id={helpTextId}
          className={cx(stylesheet.helpText, disabled && stylesheet.disabled)}
        >
          {helpText}
        </span>
      ) : null}
    </div>
  );
});

export default Slider;
